import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, RefreshCw, BellRing, CheckCheck, UserPlus, BadgeCheck,
  ArrowUpCircle, XCircle, DollarSign, AlertTriangle, LifeBuoy, Inbox, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { getErrorMessage } from "@/lib/api";

const REFRESH_MS = 30000;

const FILTERS = [
  { value: "all", label: "All" },
  { value: "signups", label: "Signups" },
  { value: "subscriptions", label: "Subscriptions" },
  { value: "cancellations", label: "Cancellations" },
  { value: "payments_succeeded", label: "Payments" },
  { value: "payments_failed", label: "Failed payments" },
  { value: "claim_concierge", label: "Claim Concierge" },
];

const PLAN_LABEL = { free: "Free", pro: "Pro Host", pm: "Property Manager" };
const INTERVAL_LABEL = { monthly: "Monthly", "6mo": "6 Months", "12mo": "12 Months" };

// Visual config per notification type. Colours use existing risk/brand tokens.
const TYPE_META = {
  signup_free: { icon: UserPlus, tone: "muted", label: "New signup" },
  subscription_started_pro: { icon: BadgeCheck, tone: "low", label: "New subscription" },
  subscription_started_pm: { icon: BadgeCheck, tone: "primary", label: "New subscription" },
  upgrade_free_to_pro: { icon: ArrowUpCircle, tone: "primary", label: "Plan change" },
  upgrade_pro_to_pm: { icon: ArrowUpCircle, tone: "primary", label: "Upgrade" },
  subscription_cancelled: { icon: XCircle, tone: "medium", label: "Cancellation" },
  payment_succeeded: { icon: DollarSign, tone: "low", label: "Payment received" },
  payment_failed: { icon: AlertTriangle, tone: "high", label: "Payment failed" },
  claim_concierge: { icon: LifeBuoy, tone: "primary", label: "Claim Concierge" },
};

const TONE_CLASS = {
  low: "bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]",
  medium: "bg-[hsl(var(--risk-medium-bg))] text-[hsl(var(--risk-medium))]",
  high: "bg-[hsl(var(--risk-high-bg))] text-[hsl(var(--risk-high))]",
  primary: "bg-primary/10 text-primary",
  muted: "bg-muted text-muted-foreground",
};

const fmtMoney = (v, cur = "AUD") =>
  v === null || v === undefined ? null : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${String(cur).toUpperCase()}`;

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const Field = ({ label, value, testid }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex min-w-0 flex-col" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="break-words text-sm font-medium text-foreground" title={typeof value === "string" ? value : undefined}>{value}</span>
    </div>
  );
};

const NotificationCard = ({ n, onMarkRead }) => {
  const meta = TYPE_META[n.type] || { icon: BellRing, tone: "muted", label: n.type };
  const Icon = meta.icon;
  return (
    <div
      className={`flex gap-4 rounded-xl border bg-card p-4 transition-colors ${n.read ? "" : "border-primary/30 bg-primary/[0.03]"}`}
      data-testid={`notification-card-${n.id}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${TONE_CLASS[meta.tone]}`}>
        <Icon className="h-5 w-5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-display text-sm font-semibold text-foreground" data-testid="notification-title">{n.title}</p>
          <Badge variant="secondary" className="text-[10px]">{meta.label}</Badge>
          {!n.read && <span className="inline-block h-2 w-2 rounded-full bg-primary" data-testid="notification-unread-dot" aria-label="Unread" />}
        </div>
        {n.message && <p className="mt-0.5 text-sm text-muted-foreground">{n.message}</p>}

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="User name" value={n.user_name || "—"} testid="notification-user-name" />
          <Field label="User email" value={n.user_email || "—"} testid="notification-user-email" />
          <Field label="Plan" value={PLAN_LABEL[n.plan] || (n.plan ? n.plan : null)} testid="notification-plan" />
          <Field label="Billing cycle" value={n.billing_interval ? (INTERVAL_LABEL[n.billing_interval] || n.billing_interval) : null} />
          <Field label="Amount paid" value={fmtMoney(n.amount, n.currency)} testid="notification-amount" />
          <Field label="Status" value={n.subscription_status || null} />
          <Field label="Date / time" value={fmtDateTime(n.created_at)} testid="notification-datetime" />
        </div>
      </div>

      {!n.read && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 self-start text-muted-foreground hover:text-foreground"
          onClick={() => onMarkRead(n.id)}
          data-testid={`notification-mark-read-${n.id}`}
        >
          <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> Mark read
        </Button>
      )}
    </div>
  );
};

export default function AdminNotifications() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState(null);

  // Guard: redirect non-admins
  useEffect(() => {
    if (!authLoading && user && !user.is_admin) {
      navigate("/app/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const load = useCallback(async (activeFilter, isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const r = await api.get("/admin/notifications", { params: { filter: activeFilter, limit: 200 } });
      setItems(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
      setError(null);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load notifications."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    load(filter);
    const id = setInterval(() => load(filter), REFRESH_MS);
    return () => clearInterval(id);
  }, [user, filter, load]);

  const markRead = async (id) => {
    // Optimistic update
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      const r = await api.post(`/admin/notifications/${id}/read`);
      setUnread(r.data.unread_count ?? 0);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not mark as read."));
      load(filter);
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.post("/admin/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success("All notifications marked as read.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not mark all as read."));
    } finally {
      setMarkingAll(false);
    }
  };

  if (authLoading || (user && !user.is_admin)) {
    return (
      <AppShell title="Notifications">
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Notification Centre"
      action={
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(filter, true)}
            className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
            data-testid="notifications-refresh"
            aria-label="Refresh notifications"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <Button
            onClick={markAllRead}
            disabled={markingAll || unread === 0}
            className="gap-1.5"
            data-testid="notifications-mark-all-read"
          >
            {markingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />} Mark all read
          </Button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="admin-notifications-page">
        <div className="flex flex-wrap items-center gap-2">
          <BellRing className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Admin notifications</h2>
          {unread > 0 && (
            <Badge className="bg-primary/10 text-primary" data-testid="notifications-unread-badge">{unread} unread</Badge>
          )}
          <p className="w-full text-sm text-muted-foreground sm:w-auto sm:flex-1">
            Every signup, subscription, payment and cancellation across HostGuard.
          </p>
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="flex h-auto flex-wrap justify-start gap-1" data-testid="notifications-filters">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value} data-testid={`notifications-filter-${f.value}`}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--risk-high))]/25 bg-[hsl(var(--risk-high-bg))] p-3 text-sm text-[hsl(var(--risk-high))]" data-testid="notifications-error">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-card py-16 text-center" data-testid="notifications-empty">
            <Inbox className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 font-display text-base font-semibold">No notifications yet</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              New signups, subscriptions and payment events will appear here as they happen.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notifications-list">
            {items.map((n) => (
              <NotificationCard key={n.id} n={n} onMarkRead={markRead} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
